import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react'
import {Form as FormikForm, Formik, getIn, useField, useFormikContext} from 'formik'
import ComponentButton from '../../components/ComponentButton'
import {Col, Form, Modal} from 'react-bootstrap'
import ComponentInput from '../../components/ComponentInput'
import ComponentSelect from '../../components/ComponentSelect'
import useMutate from '../../../api/hooks/useMutate'
import {getFields, getInitialValues, getValidationSchema, useIsSubpage} from '../helpers'
import {useLocation, useNavigate} from 'react-router-dom'
import ComponentInCreation from '../../components/ComponentInCreation'
import ComponentTextarea from '../../components/ComponentTextarea'
import ComponentPrice from '../../components/ComponentPrice'
import ComponentDate from '../../components/ComponentDate'
import ComponentCheckbox from '../../components/ComponentCheckbox'
import ComponentAddress from '../../components/ComponentAddress'
import ComponentImage from '../../components/ComponentImage'
import useMutateWithFiles from '../../../api/hooks/useMutateWithFiles'
import ComponentTextEditor from '../../components/ComponentTextEditor'
import ComponentFile from '../../components/ComponentFile'
import ComponentRadio from '../../components/ComponentRadio'
import ComponentInfoStrings from '../../components/ComponentInfoStrings'
import ComponentSmartList from '../../components/ComponentSmartList'
import ComponentLayout from '../../components/ComponentLayout'
import ComponentTooltip from '../../components/ComponentTooltip'
import ComponentAnnotation from '../../components/ComponentAnnotation'
import InfoModal from './src/InfoModal'
import ComponentGooglePlaces from '../../components/ComponentGooglePlaces'
import {useHandleSubmitContext} from '../../../pages/DynamicPage'
import isEqual from 'lodash/isEqual'
import {TComponentButton} from '../../components/ComponentButton/_types'
import {TModuleForm, TModuleFormArea, TModuleFormBlock, TModuleFormField} from './_types'
import ComponentPhoneV2 from '../../components/ComponentPhoneV2'
import {useModalContext} from '../helpers/ModalContext'
import valuesCleaner from '../../helpers/valuesCleaner'
import {downloadFile} from '../../helpers/downloadFile'
import ComponentStreamPlayer from '../../components/ComponentStreamPlayer'
import ComponentObjectField from '../../components/ComponentObjectField'
import ComponentTotpConnect from '../../components/ComponentTotpConnect'
import {LinkOauthWithTotp} from '../../helpers/LinkOauthWithTotp'
import ComponentTextareaOptions from '../../components/ComponentTextareaOptions'
import {FieldPrintToggle} from './src/FieldPrintToggle'
import {useIntl} from 'react-intl'
import {clearDraft, loadDraft, makeDraftKey, saveFieldToDraft} from './helpers/draftStorage'
import ComponentHref from '../../components/ComponentHref/ComponentHref'
import SignModalWizard from '../../helpers/cryptopro/SignModalWizard'
import ComponentJsonBuilder from '../../components/ComponentJsonBuilder'
import {useAfterSubmitModal} from './helpers/useAfterSubmitModal'

//контекст
const FormContext = React.createContext<{
    isSuccess: boolean
    response?: number
    isFormPreparing: boolean
    setIsFormPreparing: (state: boolean) => void
}>({isSuccess: false, isFormPreparing: false, setIsFormPreparing: () => {}})
export const useFormContext = () => useContext(FormContext)

const DraftRestoreModal: React.FC<{
    show: boolean
    onRestore: () => void
    onDiscard: () => void
}> = ({show, onRestore, onDiscard}) => {
    return (
        <Modal
            size='lg'
            centered
            show={show}
            onHide={onDiscard}
            enforceFocus={false}
            restoreFocus={false}
            keyboard={false}
            backdrop='static'
            container={typeof document !== 'undefined' ? document.body : undefined}
            className='moduleForm_modal_draft_local_modal'
            backdropClassName='moduleForm_modal_draft_local_backdrop'
        >
            <Modal.Header>
                <Modal.Title>Внимание</Modal.Title>
            </Modal.Header>
            <Modal.Body>У вас есть несохранённые изменения. Восстановить?</Modal.Body>
            <Modal.Footer>
                <ComponentButton
                    type='custom'
                    settings={{title: 'Отменить', background: 'secondary', icon: ''}}
                    customHandler={onDiscard}
                />
                <ComponentButton
                    type='custom'
                    settings={{title: 'Открыть', background: 'dark', icon: ''}}
                    customHandler={onRestore}
                />
            </Modal.Footer>
        </Modal>
    )
}

const DraftBridge: React.FC<{
    enabled: boolean
    object: string
    command: string
    rowId?: number | string | null
    children: React.ReactNode
}> = ({enabled, object, command, rowId, children}) => {
    const {values, setValues} = useFormikContext<any>()
    const draftKey = useMemo(
        () => makeDraftKey(object, command, rowId ?? undefined),
        [object, command, rowId]
    )

    const [showRestore, setShowRestore] = useState(false)
    const [draftValues, setDraftValues] = useState<Record<string, any> | null>(null)

    // при монтировании: если есть черновик и он отличается — показать модалку
    useEffect(() => {
        if (!enabled) return
        const payload = loadDraft(draftKey)
        if (!payload || !payload.values) return

        // ищем реальные отличия
        const diff = Object.keys(payload.values).filter((k) => {
            const cur = k.split('.').reduce((acc, p) => (acc ? acc[p] : undefined), values as any)
            return !isEqual(cur, payload.values[k])
        })

        if (diff.length) {
            setDraftValues(payload.values)
            setShowRestore(true)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [draftKey, enabled])

    const handleBlurCapture = useCallback(
        (e: React.FocusEvent<HTMLElement>) => {
            if (!enabled) return
            const el = e.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
            if (
                !el?.name ||
                el.hasAttribute('disabled') ||
                el.getAttribute('aria-disabled') === 'true'
            )
                return
            // берём актуальное значение из formik.values по пути name
            const path = el.name
            const pathParts = path
                .replace(/\[(\d+)\]/g, '.$1')
                .split('.')
                .filter(Boolean)
            let cur: any = values
            for (const p of pathParts) cur = cur?.[p]
            saveFieldToDraft(draftKey, path, cur)
        },
        [enabled, values, draftKey]
    )

    const handleRestore = useCallback(() => {
        if (!draftValues) return
        // сливаем черновик в форму
        setValues((prev: any) => {
            const next = {...prev}
            Object.keys(draftValues).forEach((path) => {
                const parts = path
                    .replace(/\[(\d+)\]/g, '.$1')
                    .split('.')
                    .filter(Boolean)
                let cur = next
                for (let i = 0; i < parts.length - 1; i++) {
                    const p = parts[i]
                    if (cur[p] == null) cur[p] = /^\d+$/.test(parts[i + 1]) ? [] : {}
                    cur = cur[p]
                }
                cur[parts[parts.length - 1]] = draftValues[path]
            })
            return next
        })
        setShowRestore(false)
        setDraftValues(null)
    }, [draftValues, setValues])

    const handleDiscard = useCallback(() => {
        clearDraft(draftKey)
        setShowRestore(false)
        setDraftValues(null)
    }, [draftKey])

    return (
        <div onBlurCapture={enabled ? handleBlurCapture : undefined}>
            {children}
            <DraftRestoreModal
                show={enabled && showRestore}
                onRestore={handleRestore}
                onDiscard={handleDiscard}
            />
        </div>
    )
}

//Тип поля
export const Component = React.memo<TModuleFormField>((props) => {
    const {
        article,
        data_type,
        field_type,
        is_disabled,
        is_visible = true,
        hook,
        is_clearable,
        object_id,
        request_object,
        childButtonsForSmartList,
    } = props
    switch (field_type) {
        case 'string':
        case 'integer':
        case 'float':
        case 'email':
        case 'password':
        case 'inn':
        case 'passport_series':
        case 'passport_number':
        case 'vin':
        case 'birth_certificate':
            return (
                <ComponentInput
                    article={article}
                    field_type={field_type}
                    is_disabled={Boolean(is_disabled)}
                    hook={hook}
                    suffix={props.suffix}
                />
            )
        case 'textarea':
            return (
                <ComponentTextarea
                    article={article}
                    is_disabled={Boolean(is_disabled)}
                    rows={props.settings?.rows}
                />
            )
        case 'list':
        case 'link_list' as 'list':
        case 'color_list' as 'list':
            const {
                joined_field,
                joined_field_filter,
                search,
                settings,
                list_donor,
                on_change_submit,
            } = props
            const resolvedObject = settings?.object ?? list_donor?.table
            const resolvedSelect =
                settings?.select ?? list_donor?.select ?? list_donor?.properties_title
            const resolvedSelectMenu = settings?.select_menu ?? list_donor?.select_menu
            const resolvedIsDuplicate = settings?.is_duplicate ?? settings?.is_duplicate ?? false
            const resolvedList = props.list ?? props.custom_list ?? []

            return (
                <ComponentSelect
                    article={article}
                    data_type={data_type}
                    list={resolvedList}
                    isDisabled={Boolean(is_disabled)}
                    isVisible={Boolean(is_visible)}
                    isMulti={data_type === 'array'}
                    hook={hook}
                    joined_field={joined_field}
                    joined_field_filter={joined_field_filter}
                    isDuplicate={resolvedIsDuplicate}
                    object={resolvedObject}
                    select={resolvedSelect}
                    select_menu={resolvedSelectMenu}
                    search={search}
                    isClearable={is_clearable === false ? is_clearable : true}
                    onChangeSubmit={on_change_submit}
                    withColor={field_type === ('color_list' as 'list')}
                    is_checkboxOption={props.settings?.is_checkboxOption ?? false}
                    settings={props.settings}
                />
            )
        case 'price':
            return (
                <ComponentPrice
                    article={article}
                    data_type={data_type}
                    hook={hook}
                    is_disabled={Boolean(is_disabled)}
                />
            )
        case 'phone':
            return (
                <ComponentPhoneV2
                    article={article}
                    hook={hook}
                    is_disabled={Boolean(is_disabled)}
                    script={props.script}
                />
            )
        case 'date':
        case 'time':
        case 'datetime':
        case 'month':
        case 'year':
            return (
                <ComponentDate
                    article={article}
                    field_type={field_type}
                    is_disabled={Boolean(is_disabled)}
                    is_clearable={Boolean(is_clearable)}
                    hook={hook}
                />
            )
        case 'checkbox':
            return (
                <ComponentCheckbox
                    article={article}
                    is_disabled={Boolean(is_disabled)}
                    hook={hook}
                />
            )
        case 'dadata_address':
        case 'dadata_country':
        case 'dadata_region':
        case 'dadata_local_area':
        case 'dadata_city':
        case 'dadata_street':
        case 'dadata_passport':
            return (
                <ComponentAddress
                    article={article}
                    field_type={field_type}
                    is_disabled={Boolean(is_disabled)}
                />
            )
        case 'google_address':
            return <ComponentGooglePlaces article={article} is_disabled={Boolean(is_disabled)} />
        case 'image':
            return (
                <ComponentImage
                    article={article}
                    allowedFormats={props.settings?.allowed_formats}
                    max_size={props.settings?.max_size}
                    is_multiply={props.settings?.is_multiply}
                    is_editor={props.settings?.is_editor}
                    is_disabled={Boolean(is_disabled)}
                />
            )
        case 'editor':
            return <ComponentTextEditor article={article} />
        case 'file':
            return (
                <ComponentFile
                    article={article}
                    allowedFormats={props.settings?.allowed_formats}
                    max_size={props.settings?.max_size}
                    is_multiply={props.settings?.is_multiply}
                    request_object={request_object}
                    object_id={object_id}
                />
            )
        case 'radio':
            return (
                <ComponentRadio
                    article={article}
                    list={props.list}
                    is_disabled={Boolean(is_disabled)}
                    hook={hook}
                />
            )
        case 'info_strings':
            return <ComponentInfoStrings article={article} />
        case 'smart_list':
            const {properties, is_headers_shown, isTest} = props.settings
            return (
                <ComponentSmartList
                    article={article}
                    properties={properties}
                    hook={hook}
                    is_headers_shown={is_headers_shown}
                    isTest={Boolean(isTest)}
                    is_disabled={Boolean(is_disabled)}
                    buttons={childButtonsForSmartList}
                />
            )
        case 'layout':
            const is_edit = Boolean(props.settings?.is_edit)
            return <ComponentLayout article={article} is_edit={is_edit} />
        case 'stream_player':
            return (
                <ComponentStreamPlayer
                    article={article}
                    is_edit_stream_player={props?.is_edit_stream_player}
                ></ComponentStreamPlayer>
            )
        case 'object':
            return (
                <ComponentObjectField
                    article={article}
                    value={Array.isArray(props.value) ? props.value : []}
                    properties={props.settings?.properties ?? []}
                    object={props.settings?.object}
                    foreign_key={props.settings?.foreign_key}
                    hook={props.hook}
                />
            )
        case 'totp_connect':
            return (
                <ComponentTotpConnect
                    article={article}
                    uri={props.value.uri}
                    code={props.value.code}
                    is_disabled={Boolean(is_disabled)}
                />
            )
        case 'textareaOptions':
            return (
                <ComponentTextareaOptions
                    article={article}
                    is_disabled={Boolean(is_disabled)}
                    is_visible={Boolean(is_visible)}
                    settings={props.settings as any}
                    reloadKey={(props as any).reloadKey}
                    title={props.title}
                    description={props.description ?? props.annotation}
                />
            )
        case 'href':
            return <ComponentHref article={article} is_disabled={Boolean(is_disabled)} />

        case 'json_builder':
            return (
                <ComponentJsonBuilder
                    article={article}
                    is_disabled={Boolean(is_disabled)}
                    defaultJson={(props as any)?.settings?.default_json}
                />
            )

        default:
            return <ComponentInCreation type={field_type} />
    }
})

//Компонент, состоящий из поля определенного типа и лейбла
const ModuleFormField = React.memo<TModuleFormField>((props) => {
    const {
        annotation,
        title,
        is_required,
        article,
        buttons,
        size,
        object_id,
        request_object,
        isFieldDisabled,
        isFieldVisible,
        fieldDescription,
        _showPrintToggle,
        is_edit_stream_player,
    } = props

    const currentButtons = buttons.filter((button) => button?.settings?.field_place === article)
    const resolvedSize = size ? size * 3 : 12
    const resolvedContainerClassName = `moduleForm_field${!isFieldVisible ? ' disabled' : ''}${
        resolvedSize !== 12 ? ' marginless' : ''
    }`

    const childButtonsForSmartList = useMemo(() => {
        const prefix = `${article}.` // например, "purchases_products."
        return buttons.filter((b) => (b?.settings?.field_place || '').startsWith(prefix))
    }, [buttons, article])

    if (!isFieldVisible) {
        return null
    }
    return (
        <Form.Group className={resolvedContainerClassName} as={Col} md={resolvedSize}>
            {title ? (
                <Form.Group className='moduleForm_field_labelContainer' as={Col} md={12}>
                    <ComponentTooltip title={title}>
                        <label
                            className={`moduleForm_field_label${is_required ? ' required' : ''}`}
                        >
                            {title}
                        </label>
                    </ComponentTooltip>
                    {annotation ? <ComponentAnnotation annotation={annotation} /> : null}
                    {_showPrintToggle && props.field_type !== 'textareaOptions' && (
                        <FieldPrintToggle
                            path={
                                (props as any)?.settings?.forPrintArticle ||
                                `printFields.${String(article).replace(/_id$/i, '')}`
                            }
                        />
                    )}
                </Form.Group>
            ) : null}
            <Form.Group className='moduleForm_field_container' as={Col} md={12}>
                <div className='moduleForm_field_component'>
                    <Component
                        {...props}
                        is_disabled={isFieldDisabled}
                        is_visible={isFieldVisible}
                        request_object={request_object}
                        object_id={object_id}
                        childButtonsForSmartList={childButtonsForSmartList}
                    />
                </div>
                {currentButtons.map((button) => (
                    <ComponentButton
                        className='moduleForm_field_button'
                        key={button.type + button.settings.title}
                        {...button}
                        customHandler={() => {}}
                        defaultLabel='icon'
                    />
                ))}
            </Form.Group>
            <span className='moduleForm_field_description'>{fieldDescription ?? ''}</span>
        </Form.Group>
    )
})
//Блок полей
const ModuleFormBlock: React.FC<TModuleFormBlock> = (props) => {
    const {title, description, fields, buttons, object_id, request_object, printParagraph, is_closeGroupsFields} = props
    const [isCollapsed, setIsCollapsed] = useState(() => is_closeGroupsFields === true)
    const intl = useIntl()
    const fieldsArticles = useMemo(() => fields.map((field) => field.article), [fields])
    const [{value: visibilityState}] = useField(`fieldsVisibility`)
    const [{value: descriptionsState}] = useField(`fieldsDescriptions`)
    const [{value: disabledState}] = useField(`fieldsDisabled`)
    const isBlockVisible = fieldsArticles.some((article) => visibilityState[article])
    const {values, setFieldValue} = useFormikContext<any>()

    const childForPrintPaths = useMemo(() => {
        return (fields || []).map(
            (f) =>
                (f as any)?.settings?.forPrintArticle ||
                `printFields.${String(f.article).replace(/_id$/i, '')}`
        )
    }, [fields])

    const childStates = childForPrintPaths.map((p) => getIn(values, p))
    const allKnown = childStates.every((v) => typeof v === 'boolean')
    const allTrue = allKnown && childStates.every((v) => v === true)
    const allFalse = allKnown && childStates.every((v) => v === false)

    const toggleBlock = useCallback(() => {
        const next = !allTrue
        childForPrintPaths.forEach((p) => {
            if (Boolean(getIn(values, p)) !== next) setFieldValue(p, next)
        })
    }, [allTrue, childForPrintPaths, setFieldValue, values])

    return (
        <div className={`moduleForm_block card ${!isBlockVisible ? 'disabled' : ''}`}>
            {title ? (
                <div
                    className={`moduleForm_block_title card-header${is_closeGroupsFields ? ' moduleForm_block_title--collapsible' : ''}`}
                    onClick={is_closeGroupsFields ? () => setIsCollapsed((v) => !v) : undefined}
                >
                    <div className='card-title justify-content-between w-100'>
                        <div className='card_title_wrapper'>
                            <h3>{title}</h3>
                            {description ? (
                                <div className='moduleForm_block_description'>{description}</div>
                            ) : null}
                        </div>
                        <div className='d-flex align-items-center gap-2'>
                            {printParagraph ? (
                                <div className='moduleForm_block_title_print d-flex align-items-center gap-2' onClick={(e) => e.stopPropagation()}>
                                    <ComponentAnnotation
                                        annotation={intl.formatMessage({id: 'FIELD.PARAGRAPH'})}
                                    />
                                    <ComponentCheckbox
                                        customChecked={allTrue}
                                        indeterminate={!allTrue && !allFalse}
                                        customHandler={toggleBlock}
                                    />
                                </div>
                            ) : null}
                            {is_closeGroupsFields ? (
                                <i className={`bi bi-chevron-down moduleForm_block_chevron${isCollapsed ? '' : ' moduleForm_block_chevron--open'}`} />
                            ) : null}
                        </div>
                    </div>
                </div>
            ) : null}
            {!isCollapsed ? (
                <div className='moduleForm_block_fields card-body'>
                    {fields.map((field) => (
                        <ModuleFormField
                            key={field.article}
                            {...field}
                            buttons={buttons}
                            request_object={request_object}
                            object_id={object_id}
                            isFieldDisabled={disabledState[field.article]}
                            isFieldVisible={visibilityState[field.article]}
                            fieldDescription={descriptionsState[field.article]}
                            _showPrintToggle={printParagraph}
                            is_edit_stream_player={props?.is_edit_stream_player}
                        />
                    ))}
                </div>
            ) : null}
        </div>
    )
}
const getBlockKey = (block: TModuleFormBlock) => {
    const concatedFieldArticles = block.fields?.reduce(
        (acc, field) => `${acc}-${field.article}`,
        ''
    )
    return concatedFieldArticles
}
const ModuleFormArea: React.FC<TModuleFormArea> = ({
    blocks,
    size,
    buttons,
    object_id,
    request_object,
    printParagraph,
    is_closeGroupsFields,
}) => {
    return (
        <Form.Group className='moduleForm_area' as={Col} md={size * 3}>
            {blocks.map((block) => (
                <ModuleFormBlock
                    key={getBlockKey(block)}
                    {...block}
                    buttons={buttons}
                    request_object={request_object}
                    object_id={object_id}
                    printParagraph={printParagraph}
                    is_closeGroupsFields={is_closeGroupsFields}
                />
            ))}
        </Form.Group>
    )
}

const getAreaKey = (area: TModuleFormArea) => {
    const concatedAreaTitles = area.blocks.reduce((acc, block) => {
        const concatedFieldArticles = block.fields?.reduce(
            (acc, field) => `${acc}-${field.article}`,
            ''
        )
        return `${acc}-${concatedFieldArticles}`
    }, '')
    return `${area.size}${concatedAreaTitles}`
}

type TModuleFormButtons = {
    className: string
    buttons: Array<TComponentButton>
    isFormPreparing: boolean
    isSubpage: boolean
    isFormInsideModal: boolean
    handleSubmit: (e?: React.FormEvent<HTMLFormElement> | undefined) => void
}

const ModuleFormButtons = React.memo<TModuleFormButtons>((props) => {
    const {className, buttons, isSubpage, isFormInsideModal, isFormPreparing, handleSubmit} = props
    const setHandleSubmit = useHandleSubmitContext()
    const resolvedClassName = `componentButton_container ${className}`

    const resolvedButtons = useMemo(() => {
        if (isSubpage && !isFormInsideModal) {
            return buttons.filter((button) => button.type !== 'submit')
        } else {
            return buttons
        }
    }, [])

    const submitButton = useMemo(() => {
        return buttons.find((button) => button.type === 'submit')
    }, [])

    useEffect(() => {
        if (isSubpage && !isFormInsideModal && submitButton) {
            setHandleSubmit({
                ...submitButton,
                type: 'submit',
                customHandler: handleSubmit,
                loading: isFormPreparing,
                disabled: isFormPreparing,
            })
            return () =>
                setHandleSubmit((prev) =>
                    isEqual(prev?.customHandler, handleSubmit) ? null : prev
                )
        }
    }, [isFormPreparing])

    return (
        <Form.Group className={resolvedClassName} as={Col} md={12}>
            {resolvedButtons.map((button) => (
                <ComponentButton
                    key={button.type + button.settings.title}
                    {...button}
                    loading={button.type === 'submit' && isFormPreparing}
                    disabled={button.type === 'submit' && isFormPreparing}
                    customHandler={handleSubmit}
                />
            ))}
        </Form.Group>
    )
})

const ModuleForm: React.FC<TModuleForm> = ({components, settings}) => {
    //контекст нужен для передачи дополнительных инит значений (прим.: модалка в расписании)
    const modalContext = useModalContext()
    const initialData = Object.assign({}, modalContext.initialData ?? {}, settings.data ?? {})

    const {
        object,
        command,
        command_type,
        areas,
        type,
        close_after_submit = true,
        is_disabled,
        printParagraph,
        is_sign,
        is_closeGroupsFields,
    } = settings

    const [showSignModal, setShowSignModal] = useState(false)

    const is_draftStorage: boolean = Boolean((settings as any)?.is_draftStorage)
    const buttons = useMemo(
        () => (Array.isArray(components) ? [] : components.buttons),
        [components]
    )
    const mainButtons = useMemo(
        () => buttons.filter((button) => !button.settings.field_place),
        [buttons]
    )
    const fieldButtons = useMemo(
        () => buttons.filter((button) => button.settings.field_place),
        [buttons]
    )
    const {
        mutate: classicMutate,
        isSuccess: isSuccessClassicMutate,
        isLoading: isClassicMutateLoading,
        data: responseOnClassicMutate,
    } = useMutate(object, command)
    const {
        mutate: mutateWithFiles,
        isSuccess: isSuccessMutateWithFiles,
        isLoading: isMutateWithFilesLoading,
        data: responseOnMutateWithFilesData,
    } = useMutateWithFiles(object, command)
    const navigate = useNavigate()
    const {pathname} = useLocation()
    const splitedUrl = pathname.slice(1).split('/')
    const id =
        (settings.data as any)?.id ??
        modalContext.initialData?.id ??
        (Number.isInteger(Number(splitedUrl[splitedUrl.length - 1]))
            ? Number(splitedUrl[splitedUrl.length - 1])
            : null)
    console.log('pathname=', pathname)
    console.log('modalContext.initialData=', modalContext.initialData)
    console.log('settings.data=', settings.data)
    console.log('initialData=', initialData)
    const resolvedRowId =
        id ??
        initialData?.id ??
        (initialData as any)?.row_id ??
        (modalContext.initialData as any)?.row_id ??
        null
    const useFormDataRequest = type === 'multipart/form-data'
    const isCreating = command_type === 'add' || command_type === 'custom'
    const mutate = useFormDataRequest ? mutateWithFiles : classicMutate
    const isSuccess = useFormDataRequest ? isSuccessMutateWithFiles : isSuccessClassicMutate
    const isLoading = useFormDataRequest ? isMutateWithFilesLoading : isClassicMutateLoading
    const response = useFormDataRequest ? responseOnMutateWithFilesData : responseOnClassicMutate

    /* функция проверяет, есть ли у формы кнопка submit с переданным url для перехода после отправки формы, и, если есть, осуществляет переход.
    Сам переход выполняется в двух случаях: после успешного запроса или при отсутствии изменении в форме редактирования (отправка запроса игнорируется)  */
    const afterSubmitAction = useCallback(
        (buttons: Array<TComponentButton>, isValuesChanged: boolean) => {
            //смысла нет разбивать на find и проверку наличия, т.к. ts не определит, что это кнопка типа submit. Переделать в дальнейшем
            const redirectUrl =
                //@ts-ignore
                buttons.find((button) => button.type === 'submit')?.settings?.href ?? ''
            if (redirectUrl && !modalContext.setShow) {
                if (redirectUrl === '[return]') {
                    const pathsArray = sessionStorage.getItem('paths') ?? JSON.stringify([])
                    const resolvedPathsArray = JSON.parse(pathsArray) as Array<string>
                    if (resolvedPathsArray.length) {
                        const currentPath = resolvedPathsArray[resolvedPathsArray.length - 1]
                        navigate(currentPath)
                    } else {
                        navigate(pathname.slice(1).split('/')[0])
                    }
                } else {
                    navigate(`/${redirectUrl}`)
                }
            } else if (modalContext.setShow && close_after_submit) {
                modalContext.setShow(false)
            } else if (modalContext.refetchPage && isValuesChanged) {
                modalContext.refetchPage()
            }
        },
        []
    )

    const {enabled: isAfterSubmitModalEnabled, modalNode: afterSubmitModalNode} =
        useAfterSubmitModal({
            buttons,
            isSuccess,
            response,
            object,
            command,
            id: resolvedRowId,
            initialId: initialData?.id,
            afterSubmitAction,
        })

    useEffect(() => {
        if (!isSuccess) return

        if (is_draftStorage) {
            const draftKey = makeDraftKey(object, command, id ?? initialData?.id)
            clearDraft(draftKey)
        }

        if (is_sign && typeof is_sign === 'object') {
            setShowSignModal(true)
            return
        }

        if (isAfterSubmitModalEnabled) {
            return
        }

        afterSubmitAction(buttons, true)

        if (typeof response?.data === 'string' && response?.data.includes('http')) {
            downloadFile(response.data)
        }

        if (modalContext.handleResponse) {
            modalContext.handleResponse(response)
        }
    }, [
        afterSubmitAction,
        buttons,
        command,
        id,
        initialData?.id,
        isSuccess,
        is_draftStorage,
        is_sign,
        modalContext,
        object,
        response,
        isAfterSubmitModalEnabled,
    ])

    const formConfiguration = useMemo(() => {
        const fields = getFields(areas)
        const initial = getInitialValues(
            fields,
            isCreating,
            id && !initialData?.id ? {...initialData, id} : initialData,
            is_disabled
        )
        if (settings.printParagraph) {
            // 2.1. Плоская карта флагов полей
            const fieldMap: Record<string, boolean> = initial.printFields ?? {}

            areas.forEach((area) => {
                area.blocks.forEach((block) => {
                    block.fields?.forEach((f) => {
                        const key =
                            (f as any)?.settings?.forPrintArticle ||
                            `printFields.${String(f.article).replace(/_id$/i, '')}`

                        if (typeof getIn(fieldMap, key) !== 'boolean') {
                            const path = key.replace(/^printFields\./, '')
                            if (!(path in fieldMap)) fieldMap[path] = true
                        }
                    })
                })
            })
            const legacyParents = initial.printParagraphs || {}
            if (legacyParents && Object.keys(legacyParents).length) {
                areas.forEach((area) => {
                    area.blocks.forEach((block) => {
                        const blockKey =
                            block.fields?.reduce((acc, f) => `${acc}-${f.article}`, '') || ''
                        const parentVal = legacyParents[blockKey]
                        if (typeof parentVal === 'boolean') {
                            block.fields?.forEach((f) => {
                                const key =
                                    (f as any)?.settings?.forPrintArticle ||
                                    `printFields.${String(f.article).replace(/_id$/i, '')}`
                                const path = key.replace(/^printFields\./, '')
                                // Если parent=false — выключаем всё в абзаце (дети — источник истины)
                                if (parentVal === false) fieldMap[path] = false
                                // Если parent=true — не трогаем: дети уже и так true по дефолту/данным
                            })
                        }
                    })
                })
            }

            initial.printFields = fieldMap
            // больше не держим printParagraphs в values
            delete (initial as any).printParagraphs
            delete (initial as any).printParagraph

            const blockMap: Record<string, string[]> = {}
            areas.forEach((area) => {
                area.blocks.forEach((block) => {
                    const arts = (block.fields ?? []).map((f) => f.article)
                    const key = '-' + arts.join('-') + '-'
                    blockMap[key] = arts
                })
            })
            ;(initial as any).printBlockFields = blockMap
        }

        return {
            initialValues: initial,
            validationSchema: getValidationSchema(fields),
        }
    }, [areas, id, initialData, isCreating, is_disabled, settings.printParagraph])

    const handleSubmit = (formValues: any) => {
        const values = valuesCleaner(formValues)
        if (isLoading) {
            return
        } else {
            if (isCreating) {
                return mutate(values)
            } else {
                let changedValues: any = {}
                for (let key in values) {
                    if (values[key] !== formConfiguration.initialValues?.[key] || key === 'id') {
                        changedValues[key] = values[key]
                    }
                }

                if (resolvedRowId != null) {
                    changedValues.id = resolvedRowId
                }

                const valuesKeysAsArray = Object.keys(changedValues)
                const sendRequest =
                    valuesKeysAsArray.length > 1 ||
                    (valuesKeysAsArray.length === 1 && !valuesKeysAsArray.includes('id'))

                if (sendRequest) {
                    console.log('resolvedRowId=', resolvedRowId)
                    console.log('changedValues=', changedValues)
                    mutate(changedValues)
                } else {
                    afterSubmitAction(buttons, false)
                }
            }
        }
    }

    const isFormInsideModal = Boolean(modalContext.insideModal)
    const buttonsContainerAdditionalClass = isFormInsideModal
        ? buttons.filter((button) => button.settings.visible !== false).length === 2
            ? 'between'
            : ''
        : 'inverse'

    const isSubpage = useIsSubpage()
    const [isFormPreparing, setIsFormPreparing] = React.useState(false)

    const contextValue = useMemo(() => {
        return {
            isSuccess,
            response: response?.data,
            isFormPreparing,
            setIsFormPreparing,
        }
    }, [isSuccess])

    return (
        <FormContext.Provider value={contextValue}>
            <Formik
                enableReinitialize
                initialValues={formConfiguration.initialValues}
                onSubmit={handleSubmit}
                validationSchema={formConfiguration.validationSchema}
            >
                {({handleSubmit}) => {
                    return (
                        <div className='moduleForm'>
                            <DraftBridge
                                enabled={is_draftStorage}
                                object={object}
                                command={command}
                                rowId={id ?? initialData?.id}
                            >
                                <FormikForm>
                                    <LinkOauthWithTotp />
                                    <PrintBridge />
                                    <div className='moduleForm_container'>
                                        {areas.map((area) => (
                                            <ModuleFormArea
                                                key={getAreaKey(area)}
                                                {...area}
                                                buttons={fieldButtons}
                                                request_object={object}
                                                object_id={id}
                                                printParagraph={printParagraph}
                                                is_closeGroupsFields={is_closeGroupsFields}
                                            />
                                        ))}
                                    </div>
                                    <ModuleFormButtons
                                        className={buttonsContainerAdditionalClass}
                                        buttons={mainButtons}
                                        handleSubmit={handleSubmit}
                                        isFormPreparing={isFormPreparing || isLoading}
                                        isSubpage={isSubpage}
                                        isFormInsideModal={isFormInsideModal}
                                    />
                                </FormikForm>
                            </DraftBridge>
                            <InfoModal />
                        </div>
                    )
                }}
            </Formik>
            {afterSubmitModalNode}
            {showSignModal && (
                <SignModalWizard
                    show={showSignModal}
                    onClose={() => setShowSignModal(false)}
                    visitId={response?.data?.id ?? id}
                    formData={formConfiguration.initialValues}
                    is_sign={is_sign ?? null}
                />
            )}
        </FormContext.Provider>
    )
}

const PrintBridge: React.FC = () => {
    const {values} = useFormikContext<any>()
    useEffect(() => {
        ;(window as any).__printValues = values
    }, [values])
    return null
}

export default ModuleForm
